import { useState } from "react";
import { Badge, Card, btnPrimary, btnSecondary, btnDanger, statusColor } from "./shared";

const thLbl = { fontSize: 11, color: "#9B9690", fontFamily: "'Manrope', sans-serif", textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 500 };

const LEASE_STATUS_COLOR = { draft: "#9B9690", sent: "#B08D57", signed: "#22c55e", declined: "#ef4444", expired: "#6b7280" };
const LEASE_STATUS_LABEL = { draft: "Draft", sent: "Sent — Awaiting Signature", signed: "Signed", declined: "Declined", expired: "Expired" };

function LeaseDetailModal({ lease, onClose, getLeasePdfUrl }) {
  const [pdfLoading, setPdfLoading] = useState(false);

  const handleDownload = async () => {
    setPdfLoading(true);
    const result = await getLeasePdfUrl(lease.id);
    setPdfLoading(false);
    if (!result.success) { alert("Could not open PDF: " + result.error); return; }
    window.open(result.url, "_blank");
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#00000099", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div className="app-modal-inner" style={{ background: "#FFFFFF", border: "1px solid #d4cfc4", borderRadius: 16, padding: 28, width: 680, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 800, fontSize: 20, color: "#163C2F" }}>{lease.tenant_name || "Untitled Lease"}</div>
            <div style={{ fontSize: 13, color: "#9B9690", marginTop: 2 }}>{lease.property_address || "—"}</div>
          </div>
          <button onClick={onClose} style={{ ...btnSecondary, padding: "4px 10px" }}>✕</button>
        </div>

        <div style={{ marginBottom: 18 }}>
          <Badge label={LEASE_STATUS_LABEL[lease.status] || lease.status} color={LEASE_STATUS_COLOR[lease.status] || "#9B9690"} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20, fontSize: 13 }}>
          <div><div style={thLbl}>Lease Term</div><div style={{ marginTop: 4 }}>{lease.month_to_month ? "Month-to-month" : `${lease.start_date || "—"} → ${lease.end_date || "—"}`}</div></div>
          <div><div style={thLbl}>Monthly Rent</div><div style={{ marginTop: 4, fontWeight: 700 }}>{lease.monthly_rent ? `$${Number(lease.monthly_rent).toLocaleString()}` : "—"}</div></div>
          <div><div style={thLbl}>Due Day</div><div style={{ marginTop: 4 }}>{lease.due_day || "—"}</div></div>
          <div><div style={thLbl}>Security Deposit</div><div style={{ marginTop: 4 }}>{lease.security_deposit ? `$${Number(lease.security_deposit).toLocaleString()}` : "—"}</div></div>
        </div>

        {lease.status === "signed" && (
          <div style={{ background: "#EAF2ED", border: "1px solid #C5DBC9", borderRadius: 10, padding: "12px 16px", marginBottom: 20, fontSize: 13 }}>
            <div style={{ fontWeight: 700, color: "#163C2F", marginBottom: 4 }}>✅ Signed by {lease.signer_typed_name}</div>
            <div style={{ color: "#6b7c6b", fontSize: 12 }}>
              {lease.signed_at ? new Date(lease.signed_at).toLocaleString("en-US", { month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : ""}
              {lease.signer_ip ? ` · IP ${lease.signer_ip}` : ""}
            </div>
          </div>
        )}

        <div style={{ marginBottom: 8 }}>
          <div style={thLbl}>Full Lease Document</div>
        </div>
        <pre style={{
          background: "#F5F1EA", border: "1px solid #e0dbd0", borderRadius: 10,
          padding: "16px 18px", fontSize: 12, color: "#2d4a2d",
          whiteSpace: "pre-wrap", fontFamily: "Georgia, 'Times New Roman', serif",
          lineHeight: 1.75, maxHeight: 320, overflowY: "auto", marginBottom: 20,
        }}>{lease.document_text || "No document text was saved for this lease."}</pre>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={onClose} style={btnSecondary}>Close</button>
          <button onClick={handleDownload} disabled={pdfLoading} style={{ ...btnPrimary, opacity: pdfLoading ? 0.6 : 1 }}>
            {pdfLoading ? "Loading…" : "📄 Download PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Leases({
  tenants, rentIncreases, leases, getLeasePdfUrl,
  setNewRentIncrease, setShowAddRentIncrease,
  applyRentIncrease, deleteRentIncrease,
  setShowLeaseWizard,
}) {
  const [viewLease, setViewLease] = useState(null);
  const activeTenants = tenants.filter(t => t.status === "active" || t.status === "month-to-month");
  const today = new Date();

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
        <button onClick={() => setShowLeaseWizard(true)} style={btnPrimary}>📜 + New Lease</button>
      </div>

      <div className="app-table-scroll">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #e0dbd0" }}>
              {["Tenant","Property","Unit","Lease Start","Lease End","Monthly Rent","Due Day","Status","Days Left",""].map(h => (
                <th key={h} style={{ ...thLbl, padding: "10px 12px", textAlign: "left", whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {activeTenants.length === 0 && (
              <tr><td colSpan={10} style={{ padding: 24, textAlign: "center", color: "#9B9690", fontSize: 13 }}>No active tenants</td></tr>
            )}
            {activeTenants.map((t, i) => {
              const dLeft = t.lease_end ? Math.max(0, Math.ceil((new Date(t.lease_end) - today) / 86400000)) : null;
              const nextRI = (rentIncreases || [])
                .filter(ri => ri.tenant === t.name && !ri.applied)
                .sort((a, b) => a.effective_date.localeCompare(b.effective_date))[0] || null;
              return (
                <tr key={i} style={{ borderBottom: "1px solid #F0EDE6", background: nextRI ? "#fffbeb" : "transparent" }}>
                  <td style={{ padding: "12px 12px" }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{t.name}</div>
                    {nextRI && (
                      <div style={{ marginTop: 4, display: "inline-flex", alignItems: "center", gap: 5, background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600, color: "#92400e" }}>
                        📈 ${Number(nextRI.new_rent).toLocaleString()} on {nextRI.effective_date}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "12px 12px", fontSize: 13, color: "#2B2B2B" }}>{t.address || "—"}</td>
                  <td style={{ padding: "12px 12px", fontSize: 13, color: "#2B2B2B" }}>{t.property || "Main"}</td>
                  <td style={{ padding: "12px 12px", fontSize: 13 }}>{t.lease_start || "—"}</td>
                  <td style={{ padding: "12px 12px", fontSize: 13 }}>{t.lease_end || <span style={{ color: "#9B9690", fontStyle: "italic" }}>M-T-M</span>}</td>
                  <td style={{ padding: "12px 12px" }}>
                    {t.tenant_type === "non_paying"
                      ? <Badge label={t.non_paying_subtype === "rent_free" ? "Rent-Free" : "Expense-Only"} color={t.non_paying_subtype === "rent_free" ? "#10b981" : "#f59e0b"} />
                      : <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700 }}>${t.rent?.toLocaleString() || "—"}</span>}
                  </td>
                  <td style={{ padding: "12px 12px", fontSize: 13, textAlign: "center" }}>{t.due_day || 1}</td>
                  <td style={{ padding: "12px 12px" }}>
                    <Badge label={t.status} color={t.status === "active" ? "#22c55e" : t.status === "month-to-month" ? "#3b82f6" : "#6b7280"} />
                  </td>
                  <td style={{ padding: "12px 12px" }}>
                    {dLeft !== null
                      ? <span style={{ fontWeight: 700, color: dLeft < 30 ? "#ef4444" : dLeft < 60 ? "#f59e0b" : "#22c55e" }}>{dLeft}d</span>
                      : <span style={{ color: "#9B9690", fontSize: 12 }}>M-T-M</span>}
                  </td>
                  <td style={{ padding: "12px 12px" }}>
                    <button
                      onClick={() => { setNewRentIncrease(s => ({ ...s, tenant: t.name })); setShowAddRentIncrease(true); }}
                      style={{ ...btnSecondary, fontSize: 11, padding: "4px 9px", whiteSpace: "nowrap" }}
                    >📈 + Increase</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {rentIncreases.length > 0 && (
        <Card style={{ marginTop: 24 }}>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 800, fontSize: 16, marginBottom: 16 }}>📈 Scheduled Rent Increases</div>
          <div className="app-table-scroll">
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #e0dbd0" }}>
                  {["Tenant","Effective Date","New Rent","Notes","Status",""].map(h => (
                    <th key={h} style={{ ...thLbl, padding: "8px 12px", textAlign: "left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rentIncreases.map((ri, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #F0EDE6" }}>
                    <td style={{ padding: "10px 12px", fontWeight: 600, fontSize: 13 }}>{ri.tenant}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13 }}>{ri.effective_date}</td>
                    <td style={{ padding: "10px 12px", fontFamily: "'Manrope', sans-serif", fontWeight: 700 }}>${Number(ri.new_rent).toLocaleString()}</td>
                    <td style={{ padding: "10px 12px", fontSize: 12, color: "#9B9690" }}>{ri.notes || "—"}</td>
                    <td style={{ padding: "10px 12px" }}>{ri.applied ? <Badge label="Applied" color="#22c55e" /> : <Badge label="Pending" color="#f59e0b" />}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        {!ri.applied && <button onClick={() => applyRentIncrease(ri)} style={{ ...btnPrimary, fontSize: 11, padding: "4px 10px" }}>Apply Now</button>}
                        <button onClick={() => deleteRentIncrease(ri.id)} style={{ ...btnDanger, padding: "4px 8px" }}>🗑</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {(leases || []).length > 0 && (
        <Card style={{ marginTop: 24 }}>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 800, fontSize: 16, marginBottom: 16 }}>📜 Generated Leases</div>
          <div className="app-table-scroll">
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #e0dbd0" }}>
                  {["Tenant","Property","Status","Created",""].map(h => (
                    <th key={h} style={{ ...thLbl, padding: "8px 12px", textAlign: "left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leases.map((lease) => (
                  <tr key={lease.id} style={{ borderBottom: "1px solid #F0EDE6" }}>
                    <td style={{ padding: "10px 12px", fontWeight: 600, fontSize: 13 }}>{lease.tenant_name || "—"}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: "#2B2B2B" }}>{lease.property_address || "—"}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <Badge label={LEASE_STATUS_LABEL[lease.status] || lease.status} color={LEASE_STATUS_COLOR[lease.status] || "#9B9690"} />
                    </td>
                    <td style={{ padding: "10px 12px", fontSize: 12, color: "#9B9690" }}>
                      {lease.created_at ? new Date(lease.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <button onClick={() => setViewLease(lease)} style={{ ...btnSecondary, fontSize: 11, padding: "4px 10px" }}>👁 View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {viewLease && (
        <LeaseDetailModal lease={viewLease} onClose={() => setViewLease(null)} getLeasePdfUrl={getLeasePdfUrl} />
      )}
    </>
  );
}
